import { all, call, delay, put, select } from "redux-saga/effects";
import api from "../../services/api";
import { Creators as CartActions } from "../ducks/cart";
import { Creators as ErrorActions } from "../ducks/error";
import { Creators as OrderActions } from "../ducks/order";
import { Creators as ProductsActions } from "../ducks/products";
import { Creators as StoreByUserActions } from "../ducks/storeByUser";
import { setQuantityBannerProducts } from "./banners";
import { setQuantityProductsFound } from "./search";
import { toast } from "react-toastify";
import history from "../../services/history";
import { addItemsToCart } from "../../services/cart";
import { getExtractProductsCart, getPromotionsTotal, temPrecoDeVenda } from "../../util/price";

const MENSAGEM_SEM_PRECO = "Produto indisponível no momento.";

function applyServerCartMetadata(cart, serverCart) {
  cart.carrinhoId = serverCart.carrinhoId;
  cart.valorFreteInformado = serverCart.valorFreteInformado;
  cart.valorFreteCalculado = serverCart.valorFreteCalculado;
  cart.lojaId = serverCart.lojaId;
  cart.origemSelecaoLoja = serverCart.origemSelecaoLoja;
  cart.enderecoId = serverCart.enderecoId;
  cart.vendaAssistida = serverCart.vendaAssistida;

  if (serverCart.cupom)
    cart.cupom = serverCart.cupom;
  else
    delete cart.cupom;
}

export function* addProductBasket({ payload }) {
  const { hasDelay = true } = payload;
  if (hasDelay) {
    yield delay(500);
  }

  const { currentProductAdded, qty: amountAdded, sum, isUpdate = false } = payload;
  let cart = yield getCartFromStorage();

  //Verifica se o produto já está na cesta.
  let itemFound = cart.itens?.find(
    (basketProduct) => basketProduct.id === currentProductAdded.id
  );

  //Tira o/os produtos já existentes na sexta
  cart.itens = cart.itens.filter(
    (item) => item.id !== currentProductAdded.id
  );

  //Aplica a quantidade final do produto na cesta.
  const finalQuantity = itemFound
    ? sum
      ? itemFound.quantidade + amountAdded
      : amountAdded
    : amountAdded;

  if (finalQuantity > 50) {
    yield put(CartActions.addProductError());
    return;
  }

  if (finalQuantity > 0 && !temPrecoDeVenda(currentProductAdded.precos)) {
    yield toast.error(MENSAGEM_SEM_PRECO);
    yield put(CartActions.addProductError());
    return;
  }

  const signed = yield select((state) => state.auth.signed);

  if (signed) {
    try {
      const savedCart = yield addItemsToCart([{
        productId: currentProductAdded.id,
        quantity: finalQuantity
      }])
      currentProductAdded.itemId = savedCart.itens[0].itemId;
      applyServerCartMetadata(cart, savedCart);
    } catch (error) {
      yield toast.error(error.response?.data?.user ? `Falha ao adicionar o produto à cesta: ${error.response.data.user}` : "Não foi possível adicionar o produto à cesta");
      yield put(CartActions.addProductError());
      return;
    }
  }

  if (!cart.itens)
    cart.itens = [];

  currentProductAdded.quantidade = finalQuantity;
  cart.itens.push(currentProductAdded);

  //remove itens with quantity 0
  cart.itens = cart.itens.filter(item => item.quantidade > 0);
  const itensOrdered = cart.itens.sort(function (a, b) {
    if (a.nome < b.nome) {
      return -1;
    }
    if (a.nome > b.nome) {
      return 1;
    }
    return 0;
  });

  cart.itens = itensOrdered;

  yield saveCartStorage(cart);
  yield put(CartActions.addProductSuccess(cart));
  yield setQuantityProducts();
  yield setQuantityProductsFound();
  yield setQuantityBannerProducts();
  yield calculateAmount();

  if (finalQuantity === 0) {
    yield toast.success("Produto removido com sucesso!");
  } else {
    yield toast.success(isUpdate ? "Produto atualizado com sucesso!" : "Produto adicionado com sucesso!");
  }
}

export function* addProductListBasket({ payload }) {
  const { currentProductListAdded } = payload;

  yield delay(500);

  if (!Array.isArray(currentProductListAdded) || currentProductListAdded.length === 0) {
    yield put(CartActions.addProductError());
    yield toast.error("Nenhum produto informado.");
    return;
  }

  let cart = yield getCartFromStorage();
  const signed = yield select((state) => state.auth.signed);

  if (!cart.itens) cart.itens = [];

  const updatedItems = [];

  for (const { product, quantityToBeAdded } of currentProductListAdded) {
    if (!product?.id || !quantityToBeAdded || quantityToBeAdded < 0) continue;

    if (!temPrecoDeVenda(product.precos)) {
      yield toast.error(`${MENSAGEM_SEM_PRECO} ${product.nome || ""}`.trim());
      continue;
    }

    const existingItem = cart.itens.find((item) => item.id === product.id);

    // Remove o produto antigo
    cart.itens = cart.itens.filter((item) => item.id !== product.id);

    const finalQuantity = existingItem
      ? existingItem.quantidade + quantityToBeAdded
      : quantityToBeAdded;

    if (finalQuantity > 50) {
      yield put(CartActions.addProductError());
      yield toast.error(`Quantidade máxima excedida para o produto: ${product.nome}`);
      return;
    }

    product.quantidade = finalQuantity;
    updatedItems.push(product);
  }


  if (signed && updatedItems.length > 0) {
    try {
      const productsToSave = updatedItems.map(item => ({
        productId: item.id,
        quantity: item.quantidade,
      }));
      const savedCart = yield addItemsToCart(productsToSave);

      updatedItems.forEach(item => {
        item.itemId = savedCart.itens.find(it => it.produtoId === item.id)?.itemId;
      });
      applyServerCartMetadata(cart, savedCart);
    } catch (error) {
      yield toast.error(
        error.response?.data?.user
          ? `Falha ao adicionar os produtos à cesta: ${error.response.data.user}`
          : "Não foi possível adicionar os produtos à cesta"
      );
      yield put(CartActions.addProductError());
      return;
    }
  }

  cart.itens = [...cart.itens, ...updatedItems];

  // Ordenar itens por nome
  cart.itens.sort((a, b) => a.nome.localeCompare(b.nome));

  yield saveCartStorage(cart);
  yield put(CartActions.addProductListSuccess(cart));
  yield setQuantityProducts();
  yield setQuantityProductsFound();
  yield setQuantityBannerProducts();
  yield calculateAmount();

  yield toast.success("Produtos adicionados com sucesso!");
}

export function* calculateAmount() {
  const cart = yield getCartFromStorage();
  const { profile } = yield select((state) => state.user);
  const customerHasFacilityProgram = profile?.programa_fidelidade || false;

  const { promotions } = getExtractProductsCart(cart.itens, customerHasFacilityProgram);
  const amount = getPromotionsTotal(promotions);

  yield put(CartActions.setAmount(amount));
}

export function* shakeAnimation() {
  yield put(CartActions.shakeAnimationStart());
  yield delay(1500);
  yield put(CartActions.shakeAnimationEnd());
}

export function* setQuantityProducts() {
  const { data: stateProducts } = yield select((state) => state.products);
  const cart = yield getCartFromStorage();

  const itens = stateProducts.map((stateProduct) => {
    const storageProduct = cart.itens?.find(
      (storageProduct) => storageProduct.id === stateProduct.id
    );

    return {
      ...stateProduct,
      quantidade: !storageProduct
        ? stateProduct.quantidade
        : stateProduct.quantidade !== storageProduct.quantidade
          ? storageProduct.quantidade
          : stateProduct.quantidade,
    };
  });

  yield put(ProductsActions.setDataSuccess(itens));
}

export function* removeProductCart({ payload }) {
  const { key, currentProductRemoved } = payload;
  const cart = yield getCartFromStorage();
  const user = yield select((state) => state.user.profile);
  const signed = yield select((state) => state.auth.signed);

  if (signed) {
    try {
      const savedCart = yield addItemsToCart([{
        productId: currentProductRemoved.id,
        quantity: 0
      }])
      applyServerCartMetadata(cart, savedCart);
    } catch (error) {
      yield toast.error(error.response?.data?.user ? `Falha ao remover o produto da cesta: ${error.response?.data?.user}` : "Não foi possível remover o produto da cesta.")
      yield put(CartActions.removeProductError());
      return;
    }
  }

  const { data: stateProducts } = yield select((state) => state.products);

  const items = cart.itens.filter(
    (_, index) => index !== key
  );

  const newStateProducts = stateProducts.map((stateProduct) => {
    return {
      ...stateProduct,
      quantidade:
        currentProductRemoved.id === stateProduct.id
          ? 0
          : stateProduct.quantidade,
    };
  });

  cart.itens = items;

  signed
    ? yield localStorage.setItem(
      `@ConexaoGAM:cart#${user.uuid}`,
      JSON.stringify(cart)
    )
    : yield localStorage.setItem(
      `@ConexaoGAM:cart`,
      JSON.stringify(cart)
    );

  if (!cart || !cart.itens || cart.itens.length === 0) {
    localStorage.removeItem("@ConexaoGAM:checkout");
  }

  yield calculateAmount();
  yield put(CartActions.removeProductSuccess(cart));
  yield put(ProductsActions.setDataSuccess(newStateProducts));
  yield toast.success('Produto removido da cesta')
}

export function* setOrderStorage(action) {
  localStorage.setItem(
    "@ConexaoGAM:checkout",
    JSON.stringify(action.payload.order)
  );
  const data = yield getOrderFromStorage();
  const cart = yield getCartFromStorage();

  yield put(CartActions.setOrderStorageSuccess({ data, cart }));
}

export function* getCartFromStorage() {
  const signed = yield select((state) => state.auth.signed);

  let cart = null;
  if (!signed) {
    cart = yield JSON.parse(localStorage.getItem(`@ConexaoGAM:cart`)) || {
      carrinhoId: null,
      itens: [],
    } || {
      carrinhoId: null,
      itens: [],
    };


  } else {
    const user = yield select((state) => state.user.profile);

    cart = yield JSON.parse(
      localStorage.getItem(`@ConexaoGAM:cart#${user?.uuid}`)
    ) || {
      carrinhoId: null,
      itens: [],
    };

  }

  if (cart && !cart.itens)
    cart.itens = [];

  return cart;
}

export function* setCartInState() {
  const signed = yield select((state) => state.auth.signed);
  let cart = null;

  if (!signed) {
    cart = yield JSON.parse(localStorage.getItem(`@ConexaoGAM:cart`)) || {
      carrinhoId: null,
      itens: [],
    };
  } else {
    const user = yield select((state) => state.user.profile);
    cart = yield JSON.parse(
      localStorage.getItem(`@ConexaoGAM:cart#${user?.uuid}`)
    ) || {
      carrinhoId: null,
      itens: [],
    };
  }

  if (!cart.itens)
    cart.itens = [];

  yield put(CartActions.addProductSuccess(cart));
}

export function* saveCartStorage(cart = {
  carrinhoId: null,
  itens: [],
}) {
  const signed = yield select((state) => state.auth.signed);
  const user = yield select((state) => state.user.profile);

  if (!signed) {
    yield localStorage.setItem(`@ConexaoGAM:cart`, JSON.stringify(cart));
  } else {
    yield localStorage.setItem(
      `@ConexaoGAM:cart#${user?.uuid}`,
      JSON.stringify(cart)
    );
  }
}

export function* syncCartState(action) {
  const user = yield select((state) => state.user.profile);

  const cart = yield JSON.parse(
    localStorage.getItem(`@ConexaoGAM:cart#${user?.uuid}`)
  ) || {
    carrinhoId: null,
    itens: [],
  };

  if (!cart.itens)
    cart.itens = [];

  yield put(CartActions.addProductSuccess(cart));
  yield reorganizeBasket();
  yield setQuantityProductsFound();
  yield setQuantityBannerProducts();
  yield setQuantityProducts();

  const data = yield getOrderFromStorage();
  yield put(CartActions.setOrderStorageSuccess({ data, cart }));

  // A remoção de `@ConexaoGAM:cart` tem dono único: `syncLocalCartToAPI`
  // (services/cart.js), que só descarta a chave depois que o servidor confirma a
  // persistência. Remover aqui apagava a cesta mesmo quando o sync tinha falhado.

  if (action.payload.redirectCheckout) {
    history.replace("/checkout/revisao");
  } else {
    history.replace("/");
  }
}

export function* getOrderFromStorage() {
  return yield JSON.parse(localStorage.getItem("@ConexaoGAM:checkout")) || null;
}

export function* removeProductsFromCart({ payload }) {
  const itemsOutOfStock = payload.itemsNotAccepted;
  const acceptedItems = payload.acceptedItems.filter(
    (item) => item.transfer === true
  );
  const cart = yield getCartFromStorage();

  const partialItemsUpdated = yield cart.itens.filter(
    (itemCart) => !itemsOutOfStock.find((item) => item.id === itemCart.id)
  );

  let itemsUpdated = yield partialItemsUpdated.map((item) => {
    const itemFound = acceptedItems.find(
      (acceptedItem) => acceptedItem.id === item.id
    );

    if (typeof itemFound !== "undefined") {
      return { ...item, quantidade: itemFound.estoque_loja };
    }

    return item;
  });

  if (itemsUpdated.length === 0) {
    localStorage.removeItem("@ConexaoGAM:checkout");
  }

  try {
    const itemsToSend = []
    itemsToSend.push(...itemsOutOfStock.map((item) => {
      return {
        productId: item.id,
        quantity: 0,
      };
    }))

    itemsToSend.push(...itemsUpdated.map((item) => {
      return {
        productId: item.id,
        quantity: item.quantidade,
      }
    }))

    const cartApi = yield addItemsToCart(itemsToSend);
    applyServerCartMetadata(cart, cartApi);
    itemsUpdated = itemsUpdated.map((item) => {
      const itemApi = cartApi.itens?.find((i) => i.produtoId === item.id);
      if (itemApi) {
        item.itemId = itemApi.itemId;
      }

      return item;
    })
  } catch (error) {
    yield toast.error('Falha ao atualizar a cesta.')
    yield put(CartActions.removeProductError());
    return
  }

  cart.itens = itemsUpdated;

  yield saveCartStorage(cart);
  yield setCartInState();
  yield setQuantityProductsFound();
  yield setQuantityProducts();
  yield calculateAmount();
  yield put(OrderActions.resetError());

  if (itemsUpdated.length > 0 && payload.onSuccess) {
    yield call(payload.onSuccess, { data: cart });
  } else {
    history.push("/carrinho");
  }
}

export function* checkOutOfStockProductsByStore() {
  const cart = yield select((state) => state.cart.data);
  const storeByUser = yield select((state) => state.storeByUser.data);

  if (!cart || cart.itens.length === 0) return;
  if (storeByUser === null) return;
  // Farmacia resolvida pelo contexto de entrega nao e escolha do cliente: sem esta guarda o
  // modal de itens sem estoque passaria a abrir no boot para todo visitante com CEP.
  if (!storeByUser.selecionadaPeloCliente) return;

  const products = yield cart.itens.map((item) => {
    return item.url;
  });

  const { data } = yield call(api.post, `/products/check-stock`, {
    store: storeByUser.uuid,
    products,
  });

  const outOfStockItems = yield data.filter((item) => item.estoque_loja === 0);

  const outOfStockItemsCart = yield cart.itens.filter((itemCart) =>
    outOfStockItems.find((item) => item.id === itemCart.id)
  );

  yield put(StoreByUserActions.setOutOfStockItems(outOfStockItemsCart));

  if (outOfStockItems.length > 0) {
    yield put(StoreByUserActions.toggleOutOfStockModal());
  }
}

export function* deleteOutOfStockItemsFromCart() {
  const cart = yield select((state) => state.cart.data);
  const outOfStockItems = yield select(
    (state) => state.storeByUser.outOfStockItems
  );

  const newItens = yield cart.itens.filter(
    (itemCart) => !outOfStockItems.find((item) => item.id === itemCart.id)
  );

  cart.itens = newItens;

  yield saveCartStorage(cart);
  yield put(CartActions.addProductSuccess(cart));
  yield put(StoreByUserActions.setOutOfStockItems([]));
  yield put(StoreByUserActions.toggleOutOfStockModal());
  yield put(CartActions.calculateAmount());
}

export function* reduceBasketProducts() {
  let finalBasket = [];
  const cart = yield getCartFromStorage();

  if (cart && cart.itens) {
    cart.itens.forEach((product) => {
      const productFound = finalBasket.find((item) => item.id === product.id);

      if (!productFound) {
        finalBasket.push(product);
      }
    });
  }

  return yield finalBasket;
}

export function* reorganizeBasket() {
  let reducedBasket = yield reduceBasketProducts();
  let items = [];
  for (let currentProductAdded of reducedBasket) {
    const amountAdded = currentProductAdded.quantidade;
    const sum = false;

    //Pega quantidade existente do produto ou coloca zero
    const existingQuantity = items.reduce((qty, basketProduct) => {
      if (basketProduct.id === currentProductAdded.id) {
        return qty + basketProduct.quantidade;
      }
      return 0;
    }, 0);

    //Verifica se o produto já está na cesta.
    let productFound = items.find(
      (basketProduct) => basketProduct.id === currentProductAdded.id
    );

    //Tira o/os produtos já existentes na sexta
    items = items.filter(
      (basketProduct) => basketProduct.id !== currentProductAdded.id
    );

    //Aplica a quantidade final do produto na cesta.
    const finalQuantity = productFound
      ? sum
        ? existingQuantity + amountAdded
        : amountAdded
      : amountAdded;

    if (finalQuantity > 50) return;

    currentProductAdded.quantidade = finalQuantity;
    items.push(currentProductAdded);
  }

  const basketOrdered = items.sort(function (a, b) {
    if (a.nome < b.nome) {
      return -1;
    }
    if (a.nome > b.nome) {
      return 1;
    }
    return 0;
  });

  const cart = yield select((state) => state.cart.data);
  cart.itens = basketOrdered;

  yield saveCartStorage(cart);
  yield put(CartActions.addProductSuccess(cart));
  yield calculateAmount();
}

export function* getProductListRequest(action) {
  const { requestedProductList, storeId } = action.payload;
  try {
    const resp = yield all(
      requestedProductList.map((e) => {
        return call(api.get, `/products/${e}`);
      })
    );
    const items = resp.map((e) => e.data);

    yield put(ProductsActions.getAlreadyRequestedProducts(items));
  } catch (err) {
    console.log(err);
    put(ErrorActions.setError("Não foi possível obter o produto"));
  }
}
